"use client"

import { useState } from "react"
import { motion } from "framer-motion"
import { Mic, Upload, Link, Loader, Volume2 } from "lucide-react"

const AudioToText = () => {
  const [audioSource, setAudioSource] = useState("url")
  const [audioUrl, setAudioUrl] = useState("")
  const [audioFile, setAudioFile] = useState(null)
  const [transcription, setTranscription] = useState("")
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")

  const handleConvert = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError("")
    setTranscription("")

    const myHeaders = new Headers()
    myHeaders.append("x-apihub-key", "dC-X6X1n75W9ul7oEz2tOmvuaww1WLB3cGiKWsfsq057qO4-Ay")
    myHeaders.append("x-apihub-host", "AI-Transcription-Speech-To-Text.allthingsdev.co")
    myHeaders.append("x-apihub-endpoint", "16ddfb89-e88e-4f1e-bbd4-0734e6fbbb75")
    myHeaders.append("Content-Type", "application/json")

    const audioData = {
      audioUrl: "",
      language: "en-US",
      audioBase64: "",
    }

    if (audioSource === "url") {
      audioData.audioUrl = audioUrl
    } else if (audioSource === "file" && audioFile) {
      const base64 = await convertFileToBase64(audioFile)
      audioData.audioBase64 = base64.split(",")[1] // Remove the data:audio/xxx;base64, part
    }

    const requestOptions = {
      method: "POST",
      headers: myHeaders,
      body: JSON.stringify(audioData),
      redirect: "follow",
    }

    try {
      const response = await fetch(
        "https://AI-Transcription-Speech-To-Text.proxy-production.allthingsdev.co/api/rapidapi/speechtotext",
        requestOptions,
      )
      const result = await response.json()

      if (response.ok) {
        setTranscription(result.data)
      } else {
        throw new Error(result.message || "Failed to transcribe audio")
      }
    } catch (error) {
      console.error("Error:", error)
      setError("An error occurred while transcribing the audio. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(reader.result)
      reader.onerror = (error) => reject(error)
    })
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      <motion.div
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="text-center mb-8"
      >
        <Volume2 className="inline-block text-indigo-600 mb-4" size={48} />
        <h1 className="text-4xl font-bold text-gray-900 dark:text-white">AI Audio to Text Converter</h1>
        <p className="text-lg text-gray-600 dark:text-gray-300 mt-2">
          Convert your audio to text with the power of AI!
        </p>
      </motion.div>

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg overflow-hidden">
        <div className="p-6">
          <form onSubmit={handleConvert}>
            <div className="mb-4">
              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={() => setAudioSource("url")}
                  className={`flex-1 py-2 px-4 rounded-full ${
                    audioSource === "url"
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                  }`}
                >
                  <Link className="inline-block mr-2" size={18} />
                  URL
                </button>
                <button
                  type="button"
                  onClick={() => setAudioSource("file")}
                  className={`flex-1 py-2 px-4 rounded-full ${
                    audioSource === "file"
                      ? "bg-indigo-600 text-white"
                      : "bg-gray-200 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                  }`}
                >
                  <Upload className="inline-block mr-2" size={18} />
                  File Upload
                </button>
              </div>
            </div>

            {audioSource === "url" ? (
              <div className="mb-4">
                <input
                  type="url"
                  placeholder="Enter audio URL"
                  value={audioUrl}
                  onChange={(e) => setAudioUrl(e.target.value)}
                  className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>
            ) : (
              <div className="mb-4">
                <input
                  type="file"
                  accept="audio/*"
                  onChange={(e) => setAudioFile(e.target.files[0])}
                  className="w-full px-4 py-2 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  required
                />
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-indigo-600 text-white py-2 px-4 rounded-md hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-opacity-50 transition-colors duration-300"
            >
              {loading ? (
                <Loader className="animate-spin inline-block mr-2" size={18} />
              ) : (
                <Mic className="inline-block mr-2" size={18} />
              )}
              {loading ? "Converting..." : "Convert to Text"}
            </button>
          </form>

          {error && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="mt-4 p-4 bg-red-100 text-red-700 rounded-md"
            >
              {error}
            </motion.div>
          )}

          {transcription && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="mt-8"
            >
              <h2 className="text-2xl font-semibold mb-4 dark:text-white">Transcription Result:</h2>
              <div className="p-4 bg-gray-100 dark:bg-gray-700 rounded-md">
                <p className="text-gray-800 dark:text-gray-200">{transcription}</p>
              </div>
            </motion.div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AudioToText

